import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  MenuItem,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  FormControlLabel,
  Switch
} from '@mui/material';
import { platformApiFetch } from './platformApi';
import { usePlatformAuth } from '../context/PlatformAuthContext';

const STATUSES = ['draft', 'active', 'deprecated'];
const ROLLOUT = ['global', 'plan', 'tenant', 'hybrid'];

const emptyForm = {
  feature_name: '',
  feature_code: '',
  description: '',
  status: 'draft',
  is_global: false,
  rollout_type: 'hybrid'
};

export default function PlatformFeatureFlagFormPage() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const { user, ready } = usePlatformAuth();

  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (ready && user?.role !== 'super_admin') {
      navigate('/platform/feature-flags', { replace: true });
    }
  }, [ready, user, navigate]);

  const load = useCallback(async () => {
    if (!isEdit) return;
    setLoading(true);
    setError(null);
    try {
      const res = await platformApiFetch(`/api/platform/feature-flags/${id}`);
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Failed to load');
      const f = json.featureFlag;
      setForm({
        feature_name: f.feature_name || '',
        feature_code: f.feature_code || '',
        description: f.description || '',
        status: f.status || 'draft',
        is_global: Boolean(f.is_global),
        rollout_type: f.rollout_type || 'hybrid'
      });
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [id, isEdit]);

  useEffect(() => {
    load();
  }, [load]);

  const set = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    const body = {
      feature_name: form.feature_name.trim(),
      feature_code: form.feature_code.trim().toLowerCase(),
      description: form.description.trim() || null,
      status: form.status,
      is_global: form.is_global,
      rollout_type: form.rollout_type
    };
    try {
      const res = await platformApiFetch(
        isEdit ? `/api/platform/feature-flags/${id}` : '/api/platform/feature-flags',
        { method: isEdit ? 'PATCH' : 'POST', body }
      );
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Save failed');
      navigate(`/platform/feature-flags/${json.featureFlag.id}`);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 2 }}>
        {isEdit ? 'Edit feature flag' : 'New feature flag'}
      </Typography>
      <Button sx={{ mb: 2 }} onClick={() => navigate(isEdit ? `/platform/feature-flags/${id}` : '/platform/feature-flags')}>
        Back
      </Button>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper component="form" onSubmit={submit} sx={{ p: 3, maxWidth: 720 }}>
        <TextField
          label="Feature name"
          fullWidth
          required
          margin="normal"
          value={form.feature_name}
          onChange={(e) => set('feature_name', e.target.value)}
        />
        <TextField
          label="Feature code"
          fullWidth
          required
          margin="normal"
          disabled={isEdit}
          helperText={isEdit ? 'Code cannot be changed after creation.' : 'Lowercase letters, numbers, underscore; must start with a letter.'}
          value={form.feature_code}
          onChange={(e) => set('feature_code', e.target.value)}
        />
        <TextField
          label="Description"
          fullWidth
          margin="normal"
          multiline
          minRows={3}
          value={form.description}
          onChange={(e) => set('description', e.target.value)}
        />
        <FormControl fullWidth margin="normal">
          <InputLabel>Status</InputLabel>
          <Select label="Status" value={form.status} onChange={(e) => set('status', e.target.value)}>
            {STATUSES.map((s) => (
              <MenuItem key={s} value={s}>
                {s}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControlLabel
          control={<Switch checked={form.is_global} onChange={(e) => set('is_global', e.target.checked)} />}
          label="Global (on for all tenants when status is active)"
          sx={{ mt: 1, display: 'block' }}
        />
        <FormControl fullWidth margin="normal">
          <InputLabel>Rollout type</InputLabel>
          <Select label="Rollout type" value={form.rollout_type} onChange={(e) => set('rollout_type', e.target.value)}>
            {ROLLOUT.map((r) => (
              <MenuItem key={r} value={r}>
                {r}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
          Saving forces rollout to <code>global</code> when global is on, and away from <code>global</code> when global is
          off.
        </Typography>
        <Button type="submit" variant="contained" disabled={saving}>
          {saving ? <CircularProgress size={22} /> : 'Save'}
        </Button>
      </Paper>
    </Box>
  );
}
